/* eslint-disable no-magic-numbers */

import {
  Button,
  Card,
  formatNumber,
  Inline,
  Skeleton,
  Stack,
  type TabProps,
  Tabs,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  useContractorPayment,
  useGetContractorInvoice,
} from '@omnipresentgroup/domains';
import { ContractorInvoiceStateEnum } from '@omnipresentgroup/types';
import { useState } from 'react';

import { ContractorInvoiceAttachments } from '../contractor-invoice-attachments';
import { ContractorInvoiceQuote } from '../contractor-invoice-quote';
import type { StatusOverride } from '../contractor-invoice-status';
import {
  ContractorInvoiceStatusDetails,
  TransactionDetails,
} from './components';
import { TransactionCancelationModal } from './components/TransactionCancelationModal';

const OVERVIEW_TAB = 'overview';
const TRANSACTION_DETAILS_TAB = 'transaction-details';
const TABS: TabProps[] = [
  {
    id: OVERVIEW_TAB,
    title: 'Overview',
  },
  {
    id: TRANSACTION_DETAILS_TAB,
    title: 'Transaction details',
  },
];

type Props = {
  invoiceId: string;
  invoiceStatusOverride?: StatusOverride;
  showPaymentDetails?: boolean;
};

export const ContractorInvoiceDetails = ({
  invoiceId,
  invoiceStatusOverride,
  showPaymentDetails = false,
}: Props) => {
  const [selectedTab, setSelectedTab] = useState<TabProps>(TABS[0]);
  const [modalOpen, setModalOpen] = useState(false);

  const { data: invoice, isLoading: isInvoiceLoading } =
    useGetContractorInvoice({
      id: invoiceId,
      enabled: Boolean(invoiceId),
    });

  const { data: paymentData, isLoading: isPaymentDataLoading } =
    useContractorPayment({
      id: invoice?.paymentId,
      enabled: !!invoice?.paymentId && showPaymentDetails,
    });

  const CustomFieldLabel = ({ id, label }: { id: string; label: string }) => {
    return (
      <Typography as="label" size="12" weight="medium" color="subtle" id={id}>
        {label}
      </Typography>
    );
  };

  const Description = () => {
    const id = 'invoice-description';
    return (
      <Stack gap="8">
        <CustomFieldLabel id={id} label="Description" />
        <Typography aria-labelledby={id} as="span" size="14">
          {invoice?.description || '-'}
        </Typography>
      </Stack>
    );
  };

  const InvoicedAmount = () => {
    const id = 'invoice-amount';
    return (
      <Inline justify="flex-end">
        <Stack gap="0" align="flex-end">
          <CustomFieldLabel id={id} label="Invoiced amount" />
          <Typography aria-labelledby={id} as="span" size="20" weight="medium">
            {formatNumber({
              input: Number(invoice?.amount),
              currency: invoice?.currency,
            })}
          </Typography>
        </Stack>
      </Inline>
    );
  };

  const Overview = () => (
    <Card p="24">
      <Stack gap="24">
        <Description />
        <ContractorInvoiceAttachments invoiceId={invoiceId} />
        {paymentData?.quote ? (
          <ContractorInvoiceQuote
            quoteSummary={paymentData.quote.invoiceBreakdown.find(
              (item) => item.invoiceId === invoiceId,
            )}
          />
        ) : (
          <InvoicedAmount />
        )}
      </Stack>
    </Card>
  );

  const showTransactionDetails =
    showPaymentDetails && invoice?.paymentId && paymentData;

  return (
    <Inline justify="center">
      <Stack gap="24" maxW={600} w="stretch">
        {isInvoiceLoading || !invoice ? (
          <Skeleton height={48} />
        ) : (
          <ContractorInvoiceStatusDetails
            invoice={invoice}
            statusOverride={invoiceStatusOverride}
          />
        )}
        {showTransactionDetails ? (
          <>
            <Tabs
              onTabClicked={(tab) => setSelectedTab(tab)}
              tabsToDisplay={TABS}
            />
            {selectedTab.id === OVERVIEW_TAB && <Overview />}
            {selectedTab.id === TRANSACTION_DETAILS_TAB && (
              <Stack gap="24">
                <TransactionDetails
                  payment={paymentData}
                  loading={isPaymentDataLoading}
                />
                {paymentData.id &&
                  invoice.state === ContractorInvoiceStateEnum.IN_PROGRESS && (
                    <>
                      <Inline justify="flex-end">
                        <Button
                          variant="secondary"
                          onClick={() => setModalOpen(true)}
                        >
                          Cancel transaction
                        </Button>
                      </Inline>
                      {modalOpen && (
                        <TransactionCancelationModal
                          modalProps={{
                            open: modalOpen,
                            onOpenChange: setModalOpen,
                          }}
                          handleCancel={() => {
                            setModalOpen(false);
                          }}
                          handleConfirm={() => {
                            setModalOpen(false);
                          }}
                          payment={paymentData}
                        />
                      )}
                    </>
                  )}
              </Stack>
            )}
          </>
        ) : (
          <Overview />
        )}
      </Stack>
    </Inline>
  );
};
